import { isEqual } from 'lodash';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ArrowDown, ArrowUp, MessageSquare, Minus } from 'react-feather';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Row,
  UncontrolledPopover,
} from 'reactstrap';
import {
  Campaign,
  CustomerDataColumn,
  CustomerDataColumnOwner,
  Me,
  Organization,
  Person,
} from 'types';
import { useConfirmApi, useProxyParameters } from 'utils/api/ApiHooks';
import {
  getPhaseIndexByType,
  SORTABLE_FIELD_SUFFIX,
} from 'utils/models/Campaign';
import { PHASE_TYPE_CALIBRATION } from 'utils/models/PerformanceUtils';
import { useIsCalibrationViewAdmin } from 'utils/models/User';
import { ColumnDescriptor } from 'views/Widgets/People/FilterablePeopleTable/FilterablePeopleTableColumnSelectorModal/types';
import TeamDashboard from '../../TeamDashboard';
import { getCalibrationView } from '../../TeamDashboard/common';
import { CustomColumnGenerator } from '../../TeamDashboard/helpers';
import { calibrationViewsApi } from '../api/calibrationViewsApi';
import { CALIBRATION_STATUS_META } from '../constants';
import {
  CalibrationComment,
  CalibrationCommentVisibilty,
  CalibrationRecommendation,
  CalibrationViewSummary,
  CreateCalibrationCommentPayload,
  ThreadedCalibrationComment,
  UpdateCalibrationCommentPayload,
} from '../types';
import { isLive, isOwner, isOwnerOrAdmin } from '../utils/calibration';
import {
  flattenThreadedComments,
  groupCommentsByThread,
} from '../utils/comments';
import CalibrationViewModal from './CalibrationViewModal';
import { CalibrationViewPeople } from './CalibrationViewPeople';
import CommentsCell from './CommentsCell';
import CommentsModal from './CommentsModal';
import RecommendationCountCell from './RecommendationCountCell';

interface CalibrationViewProps {
  campaign?: Campaign;
  campaigns?: Campaign[];
  me?: Me | null;
  currentOrganization?: Organization;
  currentProxyPerson?: Person | null;
  selectedView?: CalibrationViewSummary;
  setPageStatusIndicator: (status: React.ReactNode) => void;
  handleBackToList: () => void;
  handleUpdate: (views: CalibrationViewSummary[]) => void;
}

type OwnedColumnsResponse = {
  owned_columns: (CustomerDataColumn & {
    owner_persons?: CustomerDataColumnOwner[];
  })[];
} | null;

const processOwnedCustomerColumns = (
  response: OwnedColumnsResponse
): CustomerDataColumn[] => response?.owned_columns ?? [];

const countCommentsRecursively = (
  threadedComments: ThreadedCalibrationComment[]
): number => {
  return threadedComments.reduce((total, comment) => {
    return (
      total +
      1 +
      (comment.replies?.length ? countCommentsRecursively(comment.replies) : 0)
    );
  }, 0);
};

const CalibrationView: React.FC<CalibrationViewProps> = ({
  campaign,
  campaigns,
  me,
  currentOrganization,
  currentProxyPerson,
  selectedView,
  setPageStatusIndicator,
  handleBackToList,
  handleUpdate,
}) => {
  const { formatMessage, formatDate } = useIntl();
  const proxyParameters = useProxyParameters();
  const [calibrationView, setCalibrationView] = useState<
    CalibrationViewSummary | undefined
  >(selectedView);
  const [dataVersion, setDataVersion] = useState<number>(0);
  const [data, setData] = useState<{ [key: string]: any }>({});
  const [smallHistogram, setSmallHistogram] = useState<ReactNode | undefined>(
    undefined
  );
  const [dashboardColumns, setDashboardColumns] = useState<ColumnDescriptor[]>(
    []
  );
  const [averagesSummary, setAveragesSummary] = useState<ReactNode | undefined>(
    undefined
  );
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showPrivateComments, setShowPrivateComments] =
    useState<boolean>(false);
  const ownedColumnsParams = useMemo(
    () => ({ campaign_id: campaign?.id }),
    [campaign?.id]
  );

  const { data: ownedCustomerColumns = [] } = useConfirmApi<
    CustomerDataColumn[],
    OwnedColumnsResponse
  >({
    method: 'GET',
    url: '/customer-campaign-data/columns',
    params: ownedColumnsParams,
    disabled: !campaign?.id || !currentOrganization?.id,
    postProcess: processOwnedCustomerColumns,
  });

  const allColumns = useMemo(() => {
    if (!dashboardColumns?.length) return undefined;

    return dashboardColumns.map((x) => x.field);
  }, [dashboardColumns]);

  const visibleColumnsOrDefault = useMemo(() => {
    return calibrationView?.config?.visible_columns?.length
      ? calibrationView?.config?.visible_columns
      : allColumns;
  }, [calibrationView, allColumns]);

  const statusMeta = calibrationView
    ? CALIBRATION_STATUS_META(formatMessage)[calibrationView.status]
    : null;
  const statusLabel = statusMeta ? statusMeta.name : null;
  const StatusIconComponent = statusMeta?.Icon;

  const calibrationAdmin = useIsCalibrationViewAdmin();

  const canEditView = useMemo(() => {
    if (!calibrationView) {
      return false;
    }

    // Get the person ID to check - prefer currentProxyPerson, fallback to me
    const personToCheck = currentProxyPerson || me;

    if (!personToCheck) {
      return false;
    }

    return calibrationAdmin || isOwnerOrAdmin(personToCheck, calibrationView);
  }, [calibrationView, currentProxyPerson, me, calibrationAdmin]);

  const canCalibrate = useMemo(() => {
    return canEditView && isLive(calibrationView);
  }, [canEditView, calibrationView]);

  // Owners see the full team dashboard; everyone else gets a read-only view
  // (no person profile links, no inline editing) since the view's data may be
  // filtered to a subset of the org.
  const viewerIsOwner = useMemo(
    () => isOwner(currentProxyPerson || me, calibrationView),
    [currentProxyPerson, me, calibrationView]
  );

  const isCalibrationAdmin = useMemo(
    () => !!currentOrganization?.is_system_admin || calibrationAdmin,
    [currentOrganization, calibrationAdmin]
  );

  const areCommentsReadOnly = useMemo(
    () => !isLive(calibrationView),
    [calibrationView]
  );

  const toggleIsEditing = useCallback(() => {
    setIsEditing((status) => !status);
  }, [setIsEditing]);

  const onComponentsReady = useCallback(
    ({ smallHistogram, averagesSummary, columns }) => {
      setSmallHistogram(smallHistogram);
      setAveragesSummary(averagesSummary);
      setDashboardColumns(columns);
    },
    [setSmallHistogram, setAveragesSummary, setDashboardColumns]
  );

  const commentsBySurveyResponse: {
    [key: number]: ThreadedCalibrationComment[];
  } = useMemo(() => {
    const calibration_comments: CalibrationComment[] =
      data?.calibration_comments ?? [];

    const filteredRootComments = groupCommentsByThread(
      calibration_comments,
      showPrivateComments
    );

    // Group comments by survey_response
    const groupedComments = filteredRootComments.reduce(
      (
        acc: { [key: number]: ThreadedCalibrationComment[] },
        curr: ThreadedCalibrationComment
      ) => {
        if (!acc[curr.survey_response]) {
          acc[curr.survey_response] = [];
        }
        acc[curr.survey_response].push(curr);
        return acc;
      },
      {}
    );

    return groupedComments;
  }, [data, showPrivateComments]);

  // @mention notifications link here with ?comment_id=<id>. Kept in state and
  // cleared with window.history, since any router navigation remounts the page.
  const [linkedCommentId, setLinkedCommentId] = useState<number | undefined>(
    () => {
      const param = new URLSearchParams(window.location.search).get(
        'comment_id'
      );
      return param ? Number(param) : undefined;
    }
  );

  const linkedSurveyResponse = useMemo(() => {
    const linkedComment = (data?.calibration_comments ?? []).find(
      (comment: CalibrationComment) => comment.id === linkedCommentId
    );
    if (!linkedComment) {
      return undefined;
    }
    return (data?.survey_responses ?? []).find(
      (sr) => sr.id === linkedComment.survey_response
    );
  }, [data, linkedCommentId]);

  const isLinkedCommentShown = useMemo(
    () =>
      !!linkedSurveyResponse &&
      flattenThreadedComments(
        commentsBySurveyResponse[linkedSurveyResponse.id] ?? []
      ).some((comment) => comment.id === linkedCommentId),
    [linkedSurveyResponse, commentsBySurveyResponse, linkedCommentId]
  );

  // Private comments (or replies under one) are filtered out until the toggle
  // is on. Switch it on once for the link; the user can still turn it off.
  const hasRevealedLinkedComment = useRef(false);
  useEffect(() => {
    if (
      linkedSurveyResponse &&
      !isLinkedCommentShown &&
      !hasRevealedLinkedComment.current
    ) {
      hasRevealedLinkedComment.current = true;
      setShowPrivateComments(true);
    }
  }, [linkedSurveyResponse, isLinkedCommentShown]);

  // Wait for the comment to be shown before opening, so its thread starts
  // expanded; after that the private toggle only filters inside the modal.
  const [isLinkedModalOpen, setIsLinkedModalOpen] = useState(false);
  useEffect(() => {
    if (isLinkedCommentShown) {
      setIsLinkedModalOpen(true);
    }
  }, [isLinkedCommentShown]);

  const closeLinkedComment = useCallback(() => {
    setLinkedCommentId(undefined);
    const url = new URL(window.location.href);
    url.searchParams.delete('comment_id');
    window.history.replaceState(window.history.state, '', url.toString());
  }, []);

  const togglePrivateComments = useCallback(
    () => setShowPrivateComments(!showPrivateComments),
    [setShowPrivateComments, showPrivateComments]
  );

  const showPrivateCommentsToggle = useMemo(
    () => (
      <Col className="col-auto d-flex align-items-center pb-3">
        <div
          id="show-private-comments"
          className="form-check form-switch"
          onClick={togglePrivateComments}
        >
          <Input
            name="show-private-comments-switch"
            type="checkbox"
            checked={showPrivateComments}
            onChange={togglePrivateComments}
            className={'form-check-input'}
          />
          <Label
            className="form-check-label my-0"
            htmlFor="show-private-comments-switch"
            role="button"
          >
            <FormattedMessage
              id="app.views.widgets.dashboards.calibrations.calibration_view.show_private_comments"
              defaultMessage="Show private comments"
            />
          </Label>
        </div>
        <UncontrolledPopover
          placement="top"
          trigger="hover"
          target={'show-private-comments'}
        >
          <FormattedMessage
            id="app.views.widgets.dashboards.calibrations.calibration_view.show_private_comments_help"
            defaultMessage="
              Click to {showPrivateComments, select, true {hide} other {show}} calibration comments that were set as private.
            "
            values={{ showPrivateComments }}
          />
        </UncontrolledPopover>
      </Col>
    ),
    [showPrivateComments, togglePrivateComments]
  );

  const onPostComment = useCallback(
    (
      comment: CreateCalibrationCommentPayload,
      onSuccess: (comment: CalibrationComment) => void,
      onError: (error: any) => void
    ) => {
      calibrationViewsApi.createComment(
        proxyParameters,
        { ...comment, calibration_view_id: selectedView?.id },
        (newComment) => {
          // Update local state with the new comment
          const comments = data['calibration_comments'] ?? [];
          comments.push(newComment);
          data['calibration_comments'] = comments;
          setData({ ...data });
          onSuccess(newComment);
        },
        (error) => {
          onError(error);
        }
      );
    },
    [data, setData, proxyParameters, selectedView]
  );

  const replaceComment = useCallback(
    (updatedComment: CalibrationComment) => {
      const comments: CalibrationComment[] = data['calibration_comments'] ?? [];
      const index = comments.findIndex((c) => c.id === updatedComment.id);
      comments[index] = updatedComment;
      data['calibration_comments'] = [...comments];
      setData({ ...data });
    },
    [data]
  );

  const onToggleVisibility = useCallback(
    (comment: CalibrationComment) => {
      const newVisibility =
        comment.visibility ===
        CalibrationCommentVisibilty.AUTHOR_AND_ADMINISTRATORS
          ? CalibrationCommentVisibilty.AUTHOR_AND_REVIEWERS
          : CalibrationCommentVisibilty.AUTHOR_AND_ADMINISTRATORS;

      if (!calibrationView?.id) {
        return;
      }

      calibrationViewsApi.updateComment(
        comment.id,
        calibrationView.id,
        { visibility: newVisibility },
        proxyParameters,
        replaceComment,
        () => {}
      );
    },
    [calibrationView, proxyParameters, replaceComment]
  );

  const onEditComment = useCallback(
    (
      comment: CalibrationComment,
      payload: UpdateCalibrationCommentPayload,
      onSuccess: (comment: CalibrationComment) => void,
      onError: (error: any) => void
    ) => {
      if (!calibrationView?.id) {
        return;
      }

      calibrationViewsApi.updateComment(
        comment.id,
        calibrationView.id,
        payload,
        proxyParameters,
        (updatedComment) => {
          replaceComment(updatedComment);
          onSuccess(updatedComment);
        },
        onError
      );
    },
    [calibrationView, proxyParameters, replaceComment]
  );

  const currentUserId = useMemo(() => {
    const personToCheck = currentProxyPerson || me;
    return personToCheck?.id;
  }, [currentProxyPerson, me]);

  const onEditSuccess = useCallback(
    (views: CalibrationViewSummary[]) => {
      // Find and update the current view if it's in the list
      const currentViewUpdate = views.find((v) => v.id === calibrationView?.id);
      if (currentViewUpdate) {
        setCalibrationView(currentViewUpdate);
        if (
          !isEqual(
            currentViewUpdate?.config?.reviewees_filter,
            calibrationView?.config?.reviewees_filter
          )
        ) {
          setDataVersion((version) => version + 1);
        }
      }
      if (handleUpdate) {
        handleUpdate(views);
      }
    },
    [handleUpdate, setCalibrationView, calibrationView]
  );

  const openEditViewModal = useCallback(() => {
    toggleIsEditing();
  }, [toggleIsEditing]);

  // raise/lower/keep all render the same cell, differing only in which
  // recommendation it shows. personFields is the whole row, which is where the
  // comments column has already put this person's threaded comments - so the
  // counts stay plain numbers for sorting and CSV, and only the display reads
  // the comments.
  const formatRecommendationCount = useCallback(
    (recommendation: CalibrationRecommendation) =>
      (count: number, _uniqueId: unknown, _rowIndex: unknown, row: any) => {
        const commentsValue = row?.calibration_comments;
        const surveyResponse = commentsValue?.surveyResponse;
        if (!surveyResponse) {
          return count;
        }
        return (
          <RecommendationCountCell
            key={`recommendation-${recommendation}-cell-${surveyResponse.id}`}
            recommendation={recommendation}
            count={count}
            surveyResponseId={surveyResponse.id}
            participant={surveyResponse.person}
            calibrationComments={commentsValue.comments}
            showPrivateComments={showPrivateComments}
            onToggleShowPrivate={togglePrivateComments}
            onPostComment={onPostComment}
            onToggleVisibility={onToggleVisibility}
            currentUserId={currentUserId}
            isViewAdmin={canEditView}
          ></RecommendationCountCell>
        );
      },
    [
      showPrivateComments,
      togglePrivateComments,
      onPostComment,
      onToggleVisibility,
      currentUserId,
      canEditView,
    ]
  );

  const additionalColumns: CustomColumnGenerator[] = useMemo(
    () => [
      {
        insert_after: 'draft_rating',
        columnConfig: {
          name: <MessageSquare size={14} />,
          fullName: (
            <>
              <MessageSquare size={14} />{' '}
              <FormattedMessage
                id="app.views.widgets.dashboards.calibration_dashboard.list.comments.full_name"
                defaultMessage="Calibration comments"
              />
            </>
          ),
          field: 'calibration_comments',
          sortable: true,
          // The cell value is {comments, surveyResponse}, which the sorter
          // cannot rank - it reduces an object with parseFloat and gets NaN,
          // so every row compared equal. Ranked on the count written beside
          // it by toSortValue below.
          sortField: 'calibration_comments' + SORTABLE_FIELD_SUFFIX,
          popoverContent: (
            <FormattedMessage
              id="app.views.widgets.dashboards.calibration_dashboard.list.comments.popover"
              defaultMessage="Number of comments for this person. Click to see details."
            />
          ),
          formatToCsvValue: (v) => {
            const { comments }: { comments: ThreadedCalibrationComment[] } = v;
            return countCommentsRecursively(comments);
          },
          format: (v) => {
            const { comments, surveyResponse } = v;
            return (
              <CommentsCell
                key={`comments-cell-${surveyResponse.id}`}
                surveyResponseId={surveyResponse.id}
                calibrationComments={comments}
                participant={surveyResponse.person}
                showPrivateComments={showPrivateComments}
                onToggleShowPrivate={togglePrivateComments}
                onPostComment={onPostComment}
                onToggleVisibility={onToggleVisibility}
                onEditComment={onEditComment}
                currentUserId={currentUserId}
                isViewAdmin={canEditView}
                readOnly={areCommentsReadOnly}
              ></CommentsCell>
            );
          },
          csvName: 'Calibration comments',
          type: '',
        },
        generateValue: (options) => {
          const { surveyResponse } = options;
          const allComments = commentsBySurveyResponse[surveyResponse.id] ?? [];
          return {
            comments: allComments,
            surveyResponse,
          };
        },
        // Replies included, matching formatToCsvValue - the sort and the
        // exported number should not disagree.
        toSortValue: (value) => countCommentsRecursively(value?.comments ?? []),
      },
      {
        insert_after: 'calibration_comments',
        columnConfig: {
          name: <ArrowUp className="text-success" size={14} />,
          fullName: (
            <>
              <ArrowUp className="text-success" size={14} />{' '}
              <FormattedMessage
                id="app.views.widgets.dashboards.calibration_dashboard.list.raise_column.full_name"
                defaultMessage="Calibration raise rating"
              />
            </>
          ),
          field: 'calibration_raise',
          format: formatRecommendationCount('R'),
          csvName: 'Calibration raise',
          sortable: true,
          popoverContent: (
            <FormattedMessage
              id="app.views.widgets.dashboards.calibration_dashboard.list.raise_column.popover"
              defaultMessage="Number of people that recommended to raise the rating for this person"
            />
          ),
          type: '',
        },
        generateValue: (options) => {
          const { surveyResponse } = options;
          const threadedComments =
            commentsBySurveyResponse[surveyResponse.id] ?? [];
          const allComments = flattenThreadedComments(threadedComments);
          return allComments.filter((c) => c.recommendation === 'R').length;
        },
      },
      {
        insert_after: 'calibration_raise',
        columnConfig: {
          name: <ArrowDown className="text-danger" size={14} />,
          fullName: (
            <>
              <ArrowDown className="text-danger" size={14} />{' '}
              <FormattedMessage
                id="app.views.widgets.dashboards.calibration_dashboard.list.lower.full_name"
                defaultMessage="Calibration lower rating"
              />
            </>
          ),
          csvName: 'Calibration lower',
          field: 'calibration_lower',
          format: formatRecommendationCount('L'),
          sortable: true,
          popoverContent: (
            <FormattedMessage
              id="app.views.widgets.dashboards.calibration_dashboard.list.lower_column.popover"
              defaultMessage="Number of people that recommended to lower the rating for this person"
            />
          ),
          type: '',
        },
        generateValue: (options) => {
          const { surveyResponse } = options;
          const threadedComments =
            commentsBySurveyResponse[surveyResponse.id] ?? [];
          const allComments = flattenThreadedComments(threadedComments);
          return allComments.filter((c) => c.recommendation === 'L').length;
        },
      },
      {
        insert_after: 'calibration_lower',
        columnConfig: {
          name: <Minus className="text-body" size={14} />,
          fullName: (
            <>
              <Minus className="text-body" size={14} />{' '}
              <FormattedMessage
                id="app.views.widgets.dashboards.calibration_dashboard.list.keep.full_name"
                defaultMessage="Calibration keep rating"
              />
            </>
          ),
          csvName: 'Calibration keep',
          field: 'calibration_keep',
          format: formatRecommendationCount('K'),
          sortable: true,
          popoverContent: (
            <FormattedMessage
              id="app.views.widgets.dashboards.calibration_dashboard.list.keep_column.popover"
              defaultMessage="Number of people that recommended to keep the rating for this person as it is"
            />
          ),
          type: '',
        },
        generateValue: (options) => {
          const { surveyResponse } = options;
          const threadedComments =
            commentsBySurveyResponse[surveyResponse.id] ?? [];
          const allComments = flattenThreadedComments(threadedComments);
          return allComments.filter((c) => c.recommendation === 'K').length;
        },
      },
    ],
    [
      commentsBySurveyResponse,
      showPrivateComments,
      togglePrivateComments,
      onPostComment,
      onToggleVisibility,
      onEditComment,
      currentUserId,
      canEditView,
      areCommentsReadOnly,
      formatRecommendationCount,
    ]
  );

  const dataLoader = useMemo(() => {
    if (calibrationView) {
      return getCalibrationView(calibrationView, setData);
    }
    return undefined;
  }, [calibrationView]);

  const calibrationPhaseIndex = useMemo(
    () => getPhaseIndexByType(campaign, PHASE_TYPE_CALIBRATION),
    [campaign]
  );

  return (
    <>
      <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between mb-3">
        <Button color="link" className="px-0" onClick={handleBackToList}>
          <i className="fe fe-arrow-left me-2" />
          <FormattedMessage
            id="app.views.widgets.dashboards.calibration_dashboard.list.back_button"
            defaultMessage="Back to calibration views"
          />
        </Button>
        {calibrationView && statusMeta && StatusIconComponent && (
          <div className="d-flex flex-column flex-md-row align-items-md-center gap-3 mt-3 mt-lg-0">
            <div className="d-flex align-items-center gap-2">
              <span
                className={`d-inline-flex align-items-center justify-content-center ${statusMeta.iconClassName}`}
                title={statusLabel ?? undefined}
                aria-label={statusLabel ?? undefined}
              >
                <StatusIconComponent size={20} aria-hidden="true" />
              </span>
              <span className="text-muted small">
                <FormattedMessage
                  id="app.views.widgets.dashboards.calibration_dashboard.list.due"
                  defaultMessage="Due {date}"
                  values={{
                    date: formatDate(calibrationView.due_date ?? undefined, {
                      dateStyle: 'medium',
                    }),
                  }}
                />
              </span>
            </div>
            {canEditView && (
              <Button color="primary" size="sm" onClick={openEditViewModal}>
                <FormattedMessage
                  id="app.views.widgets.dashboards.calibration_dashboard.actions.edit"
                  defaultMessage="Edit view"
                />
              </Button>
            )}
          </div>
        )}
      </div>
      <Row>
        <Card className="mb-4">
          <CardBody>
            <div className="mb-3">
              {data?.accessed_as_admin && (
                <Alert color="warning" className="mb-3">
                  <FormattedMessage
                    id="app.views.widgets.dashboards.calibration_dashboard.accessed_as_admin_banner"
                    defaultMessage="System Admin access — visibility may be limited. <link>Learn more</link>"
                    values={{
                      link: (chunks) => (
                        <span
                          id="admin-banner-learn-more"
                          className="text-link text-primary"
                          role="button"
                        >
                          {chunks}
                        </span>
                      ),
                    }}
                  />
                  <UncontrolledPopover
                    placement="bottom"
                    trigger="hover click"
                    target="admin-banner-learn-more"
                  >
                    <FormattedMessage
                      id="app.views.widgets.dashboards.calibration_dashboard.accessed_as_admin_banner.learn_more"
                      defaultMessage="You can manage all Calibration Views, but you'll only see participants and ratings you have permission to view. As a result, totals here may be lower than what Calibration View administrators and participants see."
                    />
                  </UncontrolledPopover>
                </Alert>
              )}
              <h3 className="mb-1">
                {campaign
                  ? formatMessage(
                      {
                        id: 'app.views.widgets.dashboards.calibration_dashboard.title',
                        defaultMessage: '{viewName} for {campaignName}',
                      },
                      {
                        viewName:
                          calibrationView?.name ||
                          formatMessage({
                            id: 'app.views.widgets.dashboards.calibration_dashboard.title.default_view_name',
                            defaultMessage: 'Calibration',
                          }),
                        campaignName: campaign.name,
                      }
                    )
                  : formatMessage({
                      id: 'app.views.widgets.dashboards.calibration_dashboard.no_campaign_selected',
                      defaultMessage: 'No campaign selected',
                    })}
              </h3>
              <p className="mb-0 text-muted">
                <FormattedMessage
                  id="app.views.widgets.dashboards.calibration_dashboard.subtitle"
                  defaultMessage="Track draft ratings, identify flagged participants, and ensure calibrators are ready before the session."
                />
              </p>
            </div>

            <Row className="g-3 g-xl-4 align-items-stretch">
              <Col className="d-flex">
                <CalibrationViewPeople selectedView={calibrationView} />
              </Col>

              <Col className="col-12 col-xl-auto">{averagesSummary}</Col>
              <Col className="col-12 col-xl-auto">{smallHistogram}</Col>
            </Row>
          </CardBody>
        </Card>
      </Row>
      <TeamDashboard
        // @ts-expect-error
        campaign={campaign}
        campaigns={campaigns}
        dataLoaderFunction={dataLoader}
        dataLoaderVersion={dataVersion.toString()}
        setPageStatusIndicator={setPageStatusIndicator}
        currentPhaseIndex={calibrationPhaseIndex}
        hideProgressAndAverages={true}
        disableAutoHideEmptyColumns={true}
        showRowDetailColumn={true}
        allowedColumns={visibleColumnsOrDefault}
        additionalColumns={additionalColumns}
        additionalInputs={showPrivateCommentsToggle}
        onComponentsReady={onComponentsReady}
        allowCalibrationsFromNonAdmins={canCalibrate}
        columnSelectorAdditionalText={
          <FormattedMessage
            id="app.views.widgets.dashboards.calibration_dashboard.columns_selector_helper_text"
            defaultMessage="This will only affect your view and is temporary for this session."
          />
        }
        calibrationViewId={selectedView?.id}
        disablePeopleLinks={!viewerIsOwner}
        disableEditing={!viewerIsOwner}
        stickyHeader={true}
      />
      {linkedSurveyResponse && isLinkedModalOpen && (
        <CommentsModal
          comments={commentsBySurveyResponse[linkedSurveyResponse.id] ?? []}
          isOpen={true}
          toggle={closeLinkedComment}
          participant={linkedSurveyResponse.person}
          surveyResponseId={linkedSurveyResponse.id}
          showPrivateComments={showPrivateComments}
          onToggleShowPrivate={togglePrivateComments}
          canAddCalibrationComment={!areCommentsReadOnly}
          onSubmitComment={onPostComment}
          onToggleVisibility={onToggleVisibility}
          onEditComment={onEditComment}
          currentUserId={currentUserId}
          isViewAdmin={canEditView}
          highlightedCommentId={linkedCommentId}
        />
      )}
      {isEditing && (
        <CalibrationViewModal
          me={me}
          currentProxyPerson={currentProxyPerson}
          isOpen={isEditing}
          mode={'edit'}
          adminFilter={selectedView?.config?.admin_target_audience}
          campaign={campaign}
          organizationId={currentOrganization?.id}
          editingView={calibrationView}
          onSuccess={onEditSuccess}
          onClose={toggleIsEditing}
          columns={dashboardColumns}
          visibleColumns={visibleColumnsOrDefault}
          isAdmin={isCalibrationAdmin}
          ownedCustomerColumns={ownedCustomerColumns}
          isHRBP={currentOrganization?.is_hrbp ?? false}
        />
      )}
    </>
  );
};

export default CalibrationView;
